import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { message } from 'antd';
import type { SavedPolygons } from '../../types/domain';
import { dataApiClient } from '../../infrastructure/dataApi/client';

type CreatePolygonRequest = Parameters<typeof dataApiClient.createPolygon>[0];

type UpdatePolygonRequest = Parameters<typeof dataApiClient.updatePolygon>[1];

type UseSavedPolygonData = {
  isLoading: boolean;
  isUpdating: boolean;
  savedPolygons: SavedPolygons[];
  createSavedPolygon: (data: CreatePolygonRequest) => Promise<void>;
  updateSavedPolygon: (
    id: string,
    changes: UpdatePolygonRequest
  ) => Promise<void>;
  deleteSavedPolygon: (id: string) => Promise<void>;
};

const EMPTY_DATA = [] as const satisfies SavedPolygons[];

const useSavedPolygonData = (): UseSavedPolygonData => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isFetching } = useQuery({
    queryKey: ['savedPolygon'],
    queryFn: async (): Promise<SavedPolygons[]> => {
      return dataApiClient.listPolygons();
    },
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    meta: { dataType: 'others' },
  });

  const { mutateAsync: createMutation, isPending: isCreating } = useMutation({
    mutationFn: (data: CreatePolygonRequest): Promise<void> => {
      return dataApiClient.createPolygon(data);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['savedPolygon'] });
    },
  });

  const { mutateAsync: updateMutation, isPending: isRenaming } = useMutation({
    mutationFn: ({
      id,
      changes,
    }: {
      id: string;
      changes: UpdatePolygonRequest;
    }): Promise<void> => {
      return dataApiClient.updatePolygon(id, changes);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['savedPolygon'] });
    },
    onError: (): void => {
      message.error(t('message.failedToUpdateName'));
    },
  });

  const { mutateAsync: deleteMutation, isPending: isDeleting } = useMutation({
    mutationFn: (id: string): Promise<void> => {
      return dataApiClient.deletePolygon(id);
    },
    onSuccess: (): Promise<void> => {
      return queryClient.invalidateQueries({ queryKey: ['savedPolygon'] });
    },
    onError: (): void => {
      message.error(t('message.failedToDelete'));
    },
  });

  const updateSavedPolygon = useCallback(
    (id: string, changes: UpdatePolygonRequest): Promise<void> => {
      return updateMutation({ id, changes });
    },
    [updateMutation]
  );

  return {
    isLoading: isFetching,
    isUpdating: isCreating || isRenaming || isDeleting,
    savedPolygons: data ?? EMPTY_DATA,
    createSavedPolygon: createMutation,
    updateSavedPolygon: updateSavedPolygon,
    deleteSavedPolygon: deleteMutation,
  };
};

export { useSavedPolygonData };
